import type { StaffReferral } from 'hooks/api/useInternalReferrals'
import type { SignupReferralSummary } from 'hooks/api/useSignups'

/**
 * VRG-658 — how a referral is NAMED on the staff surfaces, written once so the waitlist's
 * "Referred by", the Referrals tab and the company page cannot disagree.
 */

/** Why a referral did not count. Keys are `ReferralIneligibleReason` on the API. */
export const INELIGIBLE_REASON_LABELS: Record<string, string> = {
  self_referral: 'Self-referral',
  existing_company: 'Already a customer',
  existing_lead: 'Already on the waitlist',
  insider_attributed: 'Insider referral',
  referrer_excluded: 'Referrer cannot refer',
  rejected: 'Signup rejected',
}

export const CHANNEL_LABELS: Record<string, string> = {
  link: 'Link',
  email_invite: 'Email',
  insider: 'Insider',
}

/** The prospect, in the most specific words we have: their company once it exists. */
export const prospectOf = (r: StaffReferral) => {
  const primary =
    r.referred_company?.name ?? r.invitee.company_name ?? r.invitee.name ?? r.invitee.email ?? 'Unknown'
  const secondary = r.invitee.email && r.invitee.email !== primary ? r.invitee.email : null
  return { primary, secondary }
}

/**
 * "Referred by" — a brand (company, and the member who sent it), an Insider (the
 * FirstPromoter ref), or nobody. Same three answers on every staff surface (Spencer's spec,
 * VRG-658 §1).
 */
export const referredByOf = (
  r:
    | Pick<StaffReferral, 'channel' | 'referrer' | 'insider_ref'>
    | SignupReferralSummary
    | null
    | undefined,
): { primary: string; secondary: string | null; kind: 'brand' | 'insider' } | null => {
  if (!r) return null
  if ('kind' in r) {
    if (r.kind === 'insider') return { primary: 'Insider', secondary: r.insider_ref, kind: 'insider' }
    return {
      primary: r.referrer_company_name ?? 'Former brand',
      secondary: r.referrer_user_name ?? r.referrer_user_email ?? 'Sender not recorded',
      kind: 'brand',
    }
  }
  if (r.channel === 'insider') return { primary: 'Insider', secondary: r.insider_ref, kind: 'insider' }
  return {
    primary: r.referrer.company_name ?? 'Former brand',
    secondary: r.referrer.user_name ?? r.referrer.user_email ?? 'Sender not recorded',
    kind: 'brand',
  }
}

/** One key per dialog, so a double-submit or a retry is the same request (API idempotency). */
export const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
